const PDF_PLACEMENT_VERSION = 2
const PDF_COORDINATE_SPACE = 'pdf-user-space'
const POINT_NAMES = ['topLeft', 'topRight', 'bottomRight', 'bottomLeft']

const toFiniteNumber = (value, fallback = 0) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

const normalizePoint = (point = []) => [
  toFiniteNumber(point[0]),
  toFiniteNumber(point[1]),
]

const normalizeRotation = (rotation = 0) => {
  const normalized = toFiniteNumber(rotation) % 360
  return normalized < 0 ? normalized + 360 : normalized
}

const numbersMatch = (left, right, tolerance = 0.01) =>
  Math.abs(toFiniteNumber(left) - toFiniteNumber(right)) <= tolerance

const arraysMatch = (left = [], right = [], tolerance = 0.01) =>
  left.length === right.length &&
  left.every((value, index) => numbersMatch(value, right[index], tolerance))

export const getPdfPageMetadata = (pdfPage, page = 1) => ({
  page: toFiniteNumber(page, 1),
  viewBox: Array.from(pdfPage?.view || []).map((value) =>
    toFiniteNumber(value),
  ),
  userUnit: toFiniteNumber(pdfPage?.userUnit, 1),
  rotation: normalizeRotation(pdfPage?.rotate),
})

export const viewportRectToPdfPlacement = (
  viewport,
  cssRect,
  pageMetadata,
  templateSha256 = '',
) => {
  if (!viewport?.convertToPdfPoint) {
    throw new Error('PDF viewport is required')
  }

  const left = toFiniteNumber(cssRect?.left)
  const top = toFiniteNumber(cssRect?.top)
  const width = Math.max(0, toFiniteNumber(cssRect?.width))
  const height = Math.max(0, toFiniteNumber(cssRect?.height))
  const toPdfPoint = (x, y) =>
    normalizePoint(viewport.convertToPdfPoint(x, y))

  return {
    version: PDF_PLACEMENT_VERSION,
    coordinateSpace: PDF_COORDINATE_SPACE,
    templateSha256: templateSha256 || undefined,
    page: toFiniteNumber(pageMetadata?.page, 1),
    sourcePageView: Array.from(pageMetadata?.viewBox || []).map((value) =>
      toFiniteNumber(value),
    ),
    sourceUserUnit: toFiniteNumber(pageMetadata?.userUnit, 1),
    sourceRotation: normalizeRotation(pageMetadata?.rotation),
    quad: {
      topLeft: toPdfPoint(left, top),
      topRight: toPdfPoint(left + width, top),
      bottomRight: toPdfPoint(left + width, top + height),
      bottomLeft: toPdfPoint(left, top + height),
    },
  }
}

export const pdfPlacementToViewportRect = (viewport, placement) => {
  if (!viewport?.convertToViewportPoint) {
    throw new Error('PDF viewport is required')
  }

  const viewportPoints = POINT_NAMES.map((name) =>
    normalizePoint(viewport.convertToViewportPoint(...placement.quad[name])),
  )
  const xs = viewportPoints.map(([x]) => x)
  const ys = viewportPoints.map(([, y]) => y)

  return {
    left: Math.min(...xs),
    top: Math.min(...ys),
    width: Math.max(...xs) - Math.min(...xs),
    height: Math.max(...ys) - Math.min(...ys),
  }
}

export const isPdfPlacement = (placement) =>
  placement?.version === PDF_PLACEMENT_VERSION &&
  placement?.coordinateSpace === PDF_COORDINATE_SPACE &&
  POINT_NAMES.every(
    (name) =>
      Array.isArray(placement?.quad?.[name]) &&
      placement.quad[name].length === 2,
  )

export const isPdfPlacementCompatible = (
  placement,
  pageMetadata,
  templateSha256 = '',
) => {
  if (!isPdfPlacement(placement)) return false
  if (toFiniteNumber(placement.page, 1) !== toFiniteNumber(pageMetadata?.page, 1)) {
    return false
  }
  if (
    placement.templateSha256 &&
    templateSha256 &&
    placement.templateSha256 !== templateSha256
  ) {
    return false
  }

  return (
    arraysMatch(placement.sourcePageView, pageMetadata?.viewBox) &&
    numbersMatch(placement.sourceUserUnit, pageMetadata?.userUnit, 0.0001) &&
    normalizeRotation(placement.sourceRotation) ===
      normalizeRotation(pageMetadata?.rotation)
  )
}

export const doesPdfPlacementIntersectPage = (placement, pageMetadata) => {
  if (!isPdfPlacement(placement)) return false

  const pageView = pageMetadata?.viewBox || placement.sourcePageView
  if (!Array.isArray(pageView) || pageView.length !== 4) return false

  const [x1, y1, x2, y2] = pageView.map((value) => toFiniteNumber(value))
  const pageLeft = Math.min(x1, x2)
  const pageRight = Math.max(x1, x2)
  const pageBottom = Math.min(y1, y2)
  const pageTop = Math.max(y1, y2)
  const points = POINT_NAMES.map((name) => normalizePoint(placement.quad[name]))
  const xs = points.map(([x]) => x)
  const ys = points.map(([, y]) => y)

  return (
    Math.max(...xs) > pageLeft &&
    Math.min(...xs) < pageRight &&
    Math.max(...ys) > pageBottom &&
    Math.min(...ys) < pageTop
  )
}

export const getPdfPlacementVersion = () => PDF_PLACEMENT_VERSION
